// Simplified Claude Code Chat Controller
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import authenticatedFetch from "../utils/authenticatedFetch"

export default class extends Controller {
  static targets = ["messages", "input", "typing", "messagesContainer", "timelineItemModal", "timelineItemModalContent", "fullContextToggle", "autoApplyToggle", "continueModeToggle", "pauseButton", "resumeButton", "sidebarTab", "timelineContent", "contextPills"]
  static values = { sessionId: String }
  
  connect() {
    // console.log('Claude Code Chat controller connected with sessionId:', this.sessionIdValue)
    this.setupChannel()
    this.fetchSessionSettings() // Fetch current session settings to update UI
    // Only scroll to bottom if there are messages
    if (this.hasMessagesTarget && this.messagesTarget.children.length > 0) {
      this.scrollToBottom()
    }
    
    // Initialize message deduplication tracking with timestamp windows
    this.processedMessages = new Map() // Use Map to store timestamps
    this.processedToolCalls = new Map()
    this.deduplicationWindow = 5000 // 5 second window for deduplication
    
    // Track if we're currently processing
    this.isProcessing = false
    
    // Set up keyboard shortcuts
    this.setupKeyboardShortcuts()
    
    // Listen for timeline item clicks using event delegation
    this.timelineClickHandler = (event) => {
      const timelineItem = event.target.closest('.timeline-item-clickable')
      if (timelineItem && timelineItem.dataset.itemJson) {
        try {
          // Decode base64 and parse JSON
          const decodedJson = atob(timelineItem.dataset.itemJson)
          const itemData = JSON.parse(decodedJson)
          this.showTimelineItemDetailsFromData(itemData)
        } catch (e) {
          // console.error('Failed to parse timeline item data:', e)
        }
      }
    }
    document.addEventListener('click', this.timelineClickHandler)
  }
  
  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
    
    // Remove event listener
    if (this.timelineClickHandler) {
      document.removeEventListener('click', this.timelineClickHandler)
    }
    
    // Remove keyboard shortcut listener
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler)
    }
  }
  
  setupKeyboardShortcuts() {
    this.keyboardHandler = (event) => {
      // Ctrl+C or Cmd+C (when not in input field) to interrupt
      if ((event.ctrlKey || event.metaKey) && event.key === 'c' && 
          document.activeElement !== this.inputTarget) {
        event.preventDefault()
        this.interruptExecution()
      }
      
      // Escape key to interrupt (when processing)
      if (event.key === 'Escape' && this.isProcessing) {
        event.preventDefault()
        this.interruptExecution()
      }
    }
    
    document.addEventListener('keydown', this.keyboardHandler)
  }
  
  interruptExecution() {
    if (!this.isProcessing) return
    
    // Send interrupt command to channel
    if (this.channel) {
      this.channel.perform('cancel_current')
    }
    
    // Show interrupt feedback
    this.appendSystemMessage('Interrupting command execution...')
    
    // Update UI state
    this.isProcessing = false
    this.hideInterruptButton()
  }
  
  setupChannel() {
    // console.log('Setting up ActionCable channel...')
    
    this.channel = createConsumer().subscriptions.create(
      { 
        channel: "ClaudeCodeChannel", 
        session_id: this.sessionIdValue 
      },
      {
        connected: () => {
          // console.log("Connected to ClaudeCodeChannel")
        },
        disconnected: () => {
          // console.log("Disconnected from ClaudeCodeChannel")
        },
        received: (data) => this.handleReceived(data)
      }
    )
  }
  
  sendMessage(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return
    
    // Handle slash commands
    if (message.startsWith('/')) {
      this.handleSlashCommand(message)
      return
    }
    
    // Check if channel is connected
    if (!this.channel) {
      this.showError('Connection lost. Please refresh the page.')
      return
    }
    
    // Use continue mode solely from the toggle setting
    const isContinueMode = this.hasContinueModeToggleTarget ? this.continueModeToggleTarget.checked : true
    
    // Show user message immediately
    this.appendMessage('user', message)
    
    // Get context settings
    const includeFullContext = this.hasFullContextToggleTarget ? this.fullContextToggleTarget.checked : false
    // Don't send auto_apply with every message - let the backend use the session setting
    // const autoApply = this.hasAutoApplyToggleTarget ? this.autoApplyToggleTarget.checked : false
    
    // Send to server
    // console.log('Sending message:', message, 'Continue mode:', isContinueMode)
    this.channel.perform('send_message', { 
      message: message,
      continue: isContinueMode
      // Removed: auto_apply - let backend use session setting
    })
    
    // Clear input and show typing
    this.inputTarget.value = ''
    this.showTyping()
    
    // Mark as processing and show interrupt button
    this.isProcessing = true
    this.showInterruptButton()
  }
  
  handleReceived(data) {
    console.log('Received:', data)
    
    // Log tool_use messages specifically
    if (data.type === 'tool_use' || data.tool_calls) {
      console.log('🔧 TOOL USE MESSAGE RECEIVED:', {
        type: data.type,
        tool_calls: data.tool_calls,
        broadcast_type: data.broadcast_type,
        content: data.content
      })
    }
    
    // Guard against malformed data
    if (!data || typeof data !== 'object') {
      console.error('Received malformed data:', data)
      return
    }
    
    // Skip broadcasts without a type
    if (!data.type) {
      console.warn('Received data without type:', data)
      return
    }
    
    // Debug empty content messages
    if (data.type === 'user_message' || data.type === 'message' || data.type === 'assistant_message') {
      if (!data.content || data.content === null || data.content === undefined || data.content === '') {
        console.log(`Skipping empty ${data.type} message:`, data)
        return
      }
    }
    
    switch(data.type) {
      case 'connected':
        console.log('Session connected:', data.session_state)
        break
        
      case 'message_queued':
        // Message was queued for processing
        break
        
      case 'stream':
        // Streaming output from Claude Code CLI
        if (data.content) {
          this.updateStreamingMessage(data.content)
        }
        break
        
      case 'thinking':
        // Commented out for performance
        // console.log('Received thinking message:', data)
        this.hideTyping()
        if (data.content && data.content.trim() !== '') {
          this.appendThought(data.content)
        }
        break
        
      case 'tool_use':
        this.hideTyping()
        // For tool_use messages, we rely on the appendToolUse method's deduplication
        // which is more sophisticated and content-based
        this.appendToolUse(data)
        break
        
      case 'tool_result':
        this.hideTyping()
        this.appendToolResult(data)
        break
        
      case 'file_operation':
        this.hideTyping()
        // Only show file operations with valid data
        if (data.operation && (data.operation.type || data.operation.path || data.operation.file_path)) {
          this.appendFileOperation(data.operation)
        } else {
          // console.log('Skipping file_operation with invalid data:', data)
        }
        break
        
      case 'message':
        this.hideTyping()
        if (data.content) {
          // Check if this is actually a system message (init messages)
          if (data.content.toString().startsWith('init:') || 
              (data.metadata && data.metadata.message_type === 'ClaudeCodeSDK::SystemMessage')) {
            this.appendSystemMessageStreaming(data.content)
          } else {
            // Skip regular messages to prevent duplicates - use assistant_message instead
            // console.log('Skipping generic message to prevent duplicates:', data.content.substring(0, 50))
          }
        }
        break
        
      case 'assistant_message':
        this.hideTyping()
        if (data.content) {
          // Check if this is actually a system message (init messages)
          if (data.content.toString().startsWith('init:') || 
              (data.metadata && data.metadata.message_type === 'ClaudeCodeSDK::SystemMessage')) {
            this.appendSystemMessageStreaming(data.content)
          } else {
            // Check if this message has tool_calls
            const hasToolCalls = data.tool_calls && data.tool_calls.length > 0
            
            if (hasToolCalls) {
              this.appendToolUse(data)
            } else {
              this.appendAssistantMessageStreaming(data.content)
            }
          }
        }
        break
        
      case 'system_message':
        // Only append system messages with valid content
        if (data.content && data.content.toString().trim() !== '') {
          this.appendSystemMessageStreaming(data.content)
        } else {
          // console.log('Skipping system_message with empty content:', data)
        }
        break
        
      case 'user_message':
        // Only show user messages that have meaningful content
        // Skip empty messages that come after tool calls
        if (data.content && data.content.trim() !== '' && data.content !== 'undefined' && data.content !== 'null') {
          // Only show permission request messages as system messages
          if (data.content.includes('requested permissions')) {
            this.appendSystemMessage(data.content)
          }
          // Ignore other user messages as they're already shown when sent
        } else {
          // console.log('Skipping user_message with empty/invalid content:', data)
        }
        break
        
      case 'result_message':
        this.hideTyping()
        // Display the final response if it has content
        if (data.content && data.content !== 'Conversation completed') {
          this.appendAssistantMessageStreaming(data.content)
        }
        this.finalizeAllStreamingMessages()
        this.isProcessing = false
        this.hideInterruptButton()
        break
        
      case 'session_state':
        // Update UI state based on session state
        this.updateSessionState(data.state)
        break
        
      case 'error':
        this.hideTyping()
        // Check for overloaded error and provide helpful message
        if (data.message && (data.message.includes('529') || data.message.includes('overloaded') || data.message.includes('Overloaded'))) {
          this.showError('⚠️ Claude API is temporarily overloaded due to high demand. This usually resolves within a few minutes. The system will automatically retry.')
        } else {
          this.showError(data.message)
        }
        this.isProcessing = false
        this.hideInterruptButton()
        break
        
      case 'execution_interrupted':
        this.hideTyping()
        this.isProcessing = false
        this.hideInterruptButton()
        this.appendSystemMessage(data.message || 'Command interrupted. You can continue the conversation.')
        // Ensure we're ready for new input
        if (this.hasInputTarget) {
          this.inputTarget.focus()
        }
        break
        
      case 'workers_stopped':
        this.hideTyping()
        this.isProcessing = false
        this.hideInterruptButton()
        const msg = data.stopped_count > 0 
          ? `Successfully stopped ${data.stopped_count} background worker(s)` 
          : 'No running workers found'
        this.appendSystemMessage(msg)
        break
        
      case 'session_ended':
        this.hideTyping()
        this.isProcessing = false
        this.hideInterruptButton()
        this.appendSystemMessage('✓ Session has been ended')
        // Update UI to show session is completed
        this.updateSessionState('completed')
        // Disable input
        if (this.hasInputTarget) {
          this.inputTarget.disabled = true
          this.inputTarget.placeholder = 'Session has ended'
        }
        // Update buttons
        this.updateActionButtons('completed')
        break
        
      case 'session_restarted':
        this.hideTyping()
        this.isProcessing = false
        this.hideInterruptButton()
        // Clear all messages
        if (this.hasMessagesTarget) {
          this.messagesTarget.innerHTML = ''
        }
        this.appendSystemMessage('✓ Session has been restarted. Ready for new conversation.')
        // Update UI to show session is active
        this.updateSessionState('idle')
        // Re-enable input
        if (this.hasInputTarget) {
          this.inputTarget.disabled = false
          this.inputTarget.placeholder = 'Type your message...'
          this.inputTarget.focus()
        }
        // Update buttons
        this.updateActionButtons('idle')
        break
        
      case 'changes_applied':
        this.showSuccess(`Applied ${data.applied_count} changes`)
        break
        
      case 'timeline_update':
        this.updateTimeline(data)
        break
        
      case 'command_started':
        this.addTimelineItem(data.timeline_item)
        break
        
      case 'command_completed':
        this.updateTimelineItem(data.command_id, data.timeline_item)
        break
        
      case 'context_update':
        this.updateContextInfo(data)
        break

      case 'auth_error':
        this.hideTyping()
        this.isProcessing = false
        this.hideInterruptButton()
        this.showAuthErrorBanner(data)
        break

      default:
        // Check for messages with no type but system-like content
        if (!data.type && data.content && 
            (data.content.toString().startsWith('init:') || 
             (data.metadata && data.metadata.message_type === 'ClaudeCodeSDK::SystemMessage'))) {
          this.appendSystemMessageStreaming(data.content)
        } else {
          // console.log('Unknown message type:', data.type)
        }
    }
  }
  
  // Helper method to safely append to messages
  safeAppendToMessages(html) {
    if (this.hasMessagesTarget) {
      this.messagesTarget.insertAdjacentHTML('beforeend', html)
      return true
    }
    return false
  }
  
  appendMessage(role, content, isContinueMode = false) {
    // Skip messages with problematic content
    if (!content || content === 'undefined' || content === 'null' || content === 'undefined: null' || content === null) {
      return
    }
    
    const continueIndicator = isContinueMode ? '<span class="badge badge-sm badge-accent mr-2">Continue</span>' : ''
    
    // Render user messages with markdown support, just like assistant messages
    const renderedContent = role === 'user' ? 
      `<div class="prose prose-sm max-w-none prose-invert">${this.simpleMarkdownToHtml(content)}</div>` :
      this.escapeHtml(content)
    
    const messageHtml = `
      <div class="chat chat-${role === 'user' ? 'end' : 'start'} mb-2">
        <div class="chat-bubble ${role === 'user' ? 'bg-[#007AFF] text-white' : ''}">
          ${continueIndicator}${renderedContent}
        </div>
      </div>
    `
    if (this.safeAppendToMessages(messageHtml)) {
      this.scrollToBottom()
    }
  }
  
  appendThought(content) {
    const thoughtHtml = `
      <div class="chat chat-start mb-2 opacity-70">
        <div class="chat-bubble chat-bubble-secondary italic">
          💭 ${this.escapeHtml(content)}
        </div>
      </div>
    `
    if (this.safeAppendToMessages(thoughtHtml)) {
      this.scrollToBottom()
    }
  }
  
  appendFileOperation(operation) {
    // Skip file operations with empty or problematic data
    if (!operation || (!operation.type && !operation.path && !operation.file_path)) {
      console.log('Skipping file operation with empty/invalid data:', { operation })
      return
    }
    
    // Skip operations with undefined/null values
    const type = operation.type || 'file operation'
    const path = operation.path || operation.file_path || ''
    
    if (type === 'undefined' || type === 'null' || path === 'undefined' || path === 'null') {
      console.log('Skipping file operation with undefined/null values:', { operation })
      return
    }
    
    console.log('Appending file operation:', { type, path })
    
    const operationHtml = `
      <div class="alert alert-info mb-2">
        <div>
          <i class="fas fa-file-code mr-2"></i>
          <span class="font-semibold">${this.escapeHtml(type)}:</span>
          <span class="font-mono">${this.escapeHtml(path)}</span>
        </div>
      </div>
    `
    if (this.safeAppendToMessages(operationHtml)) {
      this.scrollToBottom()
    }
  }
  
  appendToolUse(data) {
    const toolCalls = data.tool_calls || []
    if (data.content) {
      this.appendMessage('assistant', data.content)
    }
    
    toolCalls.forEach(tool => {
      // Create a unique identifier for this tool call to prevent duplicates
      // Use a hash of the tool name and input for better deduplication
      const toolCallId = this.hashToolCall(tool.name, tool.input)
      const now = Date.now()
      
      // Check if we've processed this tool call recently
      if (this.processedToolCalls.has(toolCallId)) {
        const lastSeen = this.processedToolCalls.get(toolCallId)
        if (now - lastSeen < this.deduplicationWindow) {
          console.log('Skipping duplicate tool call:', tool.name)
          return
        }
      }
      
      // Mark as processed with timestamp
      this.processedToolCalls.set(toolCallId, now)
      
      // Clean up old entries
      this.cleanupOldEntries(this.processedToolCalls)
      
      const formattedInput = this.formatToolInput(tool.input, tool.name)
      const toolHtml = `
        <div class="chat chat-start mb-2" data-tool-call-id="${toolCallId}">
          <div class="chat-bubble chat-bubble-accent">
            <div class="flex items-center gap-2 mb-1">
              <i class="fas fa-tools"></i>
              <span class="font-semibold">Using ${this.escapeHtml(tool.name)}</span>
            </div>
            ${formattedInput ? `<div class="text-xs opacity-80 mt-1">${formattedInput}</div>` : ''}
          </div>
        </div>
      `
      this.safeAppendToMessages(toolHtml)
    })
    if (this.hasMessagesTarget) {
      this.scrollToBottom()
    }
  }
  
  appendToolResult(data) {
    const toolResults = data.tool_results || []
    toolResults.forEach(result => {
      const resultHtml = `
        <div class="chat chat-start mb-2">
          <div class="chat-bubble ${result.is_error ? 'chat-bubble-error' : 'chat-bubble-success'}">
            <div class="flex items-center gap-2 mb-1">
              <i class="fas ${result.is_error ? 'fa-exclamation-triangle' : 'fa-check-circle'}"></i>
              <span class="font-semibold">Tool Result</span>
            </div>
            <div class="text-sm">${this.escapeHtml(result.content || '')}</div>
          </div>
        </div>
      `
      this.safeAppendToMessages(resultHtml)
    })
    if (this.hasMessagesTarget) {
      this.scrollToBottom()
    }
  }
  
  appendSystemMessage(content) {
    // Skip system messages with empty or problematic content
    if (!content || content === 'undefined' || content === 'null' || content === 'undefined: null' || content === null || content.toString().trim() === '') {
      console.log('Skipping system message with empty/invalid content:', { content })
      return
    }
    
    // Debug logging for system messages
    console.log('Appending system message:', content)
    
    // Convert JSON strings to markdown format
    const formattedContent = this.formatSystemMessageContent(content)
    
    // Create a system message component that will be rendered server-side
    const messageData = {
      id: `system-${Date.now()}`,
      type: 'system_message',
      role: 'system',
      content: formattedContent,
      timestamp: new Date().toISOString()
    }
    
    // Request server-rendered message
    this.renderSystemMessage(messageData)
  }
  
  updateStreamingMessage(chunk) {
    // Find or create the streaming message
    let streamingMsg = this.messagesTarget.querySelector('.streaming-message')
    
    if (!streamingMsg) {
      const html = `
        <div class="chat chat-start mb-2 streaming-message">
          <div class="chat-bubble">
            <span class="streaming-content"></span>
            <span class="loading loading-dots loading-xs ml-2"></span>
          </div>
        </div>
      `
      this.messagesTarget.insertAdjacentHTML('beforeend', html)
      streamingMsg = this.messagesTarget.querySelector('.streaming-message')
    }
    
    const contentSpan = streamingMsg.querySelector('.streaming-content')
    contentSpan.textContent += chunk
    this.scrollToBottom()
  }
  
  showTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.remove('hidden')
      this.scrollToBottom()
    }
  }
  
  hideTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.add('hidden')
    }
    
    // Also remove streaming indicator - check if messages target exists first
    if (this.hasMessagesTarget) {
      const streamingMsg = this.messagesTarget.querySelector('.streaming-message')
      if (streamingMsg) {
        streamingMsg.classList.remove('streaming-message')
        const loadingIndicator = streamingMsg.querySelector('.loading')
        if (loadingIndicator) {
          loadingIndicator.remove()
        }
      }
    }
  }
  
  showError(message) {
    const errorHtml = `
      <div class="alert alert-error mb-2">
        <i class="fas fa-exclamation-circle"></i>
        <span>${this.escapeHtml(message)}</span>
      </div>
    `
    if (this.safeAppendToMessages(errorHtml)) {
      this.scrollToBottom()
    }
  }
  
  showSuccess(message) {
    const successHtml = `
      <div class="alert alert-success mb-2">
        <i class="fas fa-check-circle"></i>
        <span>${this.escapeHtml(message)}</span>
      </div>
    `
    if (this.safeAppendToMessages(successHtml)) {
      this.scrollToBottom()
    }
  }
  
  scrollToBottom() {
    if (this.hasMessagesContainerTarget) {
      // Use requestAnimationFrame for better performance
      // and debounce to prevent excessive calls
      if (this.scrollAnimationFrame) {
        cancelAnimationFrame(this.scrollAnimationFrame)
      }
      this.scrollAnimationFrame = requestAnimationFrame(() => {
        this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
      })
    }
  }
  
  escapeHtml(text) {
    if (text === null || text === undefined) {
      return ''
    }
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  hashToolCall(toolName, toolInput) {
    // Create a simple hash of the tool name and input for deduplication
    const str = `${toolName}-${JSON.stringify(toolInput || {})}`
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return `tool-${Math.abs(hash)}`
  }
  
  cleanupOldEntries(map) {
    const now = Date.now()
    const cutoff = now - (this.deduplicationWindow * 2) // Clean up entries older than 2x the window
    
    for (const [key, timestamp] of map.entries()) {
      if (timestamp < cutoff) {
        map.delete(key)
      }
    }
  }
  
  formatToolInput(input, toolName) {
    if (!input || typeof input !== 'object') {
      return input ? `<code class="text-xs">${this.escapeHtml(String(input))}</code>` : ''
    }
    
    // Special handling for TodoWrite tool
    if (toolName === 'TodoWrite' && input.todos) {
      return this.formatTodoList(input.todos)
    }
    
    // Create a nicely formatted display based on tool type and common parameters
    let formattedLines = []
    
    // Handle file operations (Read, Edit, Write, etc.)
    if (input.file_path || input.path) {
      const filePath = input.file_path || input.path
      formattedLines.push(`<div class="mb-1"><span class="font-medium">File:</span> <code class="bg-base-300 px-1 rounded text-xs">${this.escapeHtml(filePath)}</code></div>`)
      
      // Show additional parameters for Read tool
      if (input.offset !== undefined || input.limit !== undefined) {
        const params = []
        if (input.offset !== undefined) params.push(`offset: ${input.offset}`)
        if (input.limit !== undefined) params.push(`limit: ${input.limit}`)
        formattedLines.push(`<div class="text-xs opacity-75 mb-1">Parameters: ${params.join(', ')}</div>`)
      }
    }
    
    // Handle commands (Bash tool)
    if (input.command) {
      formattedLines.push(`<div class="mb-1"><span class="font-medium">Command:</span></div>`)
      formattedLines.push(`<div class="bg-base-300 p-2 rounded text-xs font-mono mb-1"><code>${this.escapeHtml(input.command)}</code></div>`)
      if (input.description) {
        formattedLines.push(`<div class="text-xs opacity-75">${this.escapeHtml(input.description)}</div>`)
      }
    }
    
    // Handle content fields (Write, Edit tools)
    if (input.content && formattedLines.length > 0) {
      const contentPreview = input.content.length > 100 ? 
        input.content.substring(0, 100) + '...' : 
        input.content
      formattedLines.push(`<div class="text-xs opacity-75 mt-1">Content: ${contentPreview.split('\n').length} lines</div>`)
    }
    
    // Handle edit operations
    if (input.old_string || input.new_string) {
      if (input.old_string) {
        const preview = input.old_string.length > 50 ? 
          input.old_string.substring(0, 50) + '...' : 
          input.old_string
        formattedLines.push(`<div class="text-xs opacity-75">Replacing: <code class="bg-base-300 px-1 rounded">${this.escapeHtml(preview)}</code></div>`)
      }
    }
    
    // Handle search patterns (Grep tool)
    if (input.pattern) {
      formattedLines.push(`<div class="mb-1"><span class="font-medium">Pattern:</span> <code class="bg-base-300 px-1 rounded text-xs">${this.escapeHtml(input.pattern)}</code></div>`)
      if (input.include) {
        formattedLines.push(`<div class="text-xs opacity-75">Include: ${this.escapeHtml(input.include)}</div>`)
      }
    }
    
    // Handle glob patterns
    if (input.pattern && toolName === 'Glob') {
      formattedLines.push(`<div class="mb-1"><span class="font-medium">Glob Pattern:</span> <code class="bg-base-300 px-1 rounded text-xs">${this.escapeHtml(input.pattern)}</code></div>`)
    }
    
    // Handle URLs (WebFetch tool)
    if (input.url) {
      formattedLines.push(`<div class="mb-1"><span class="font-medium">URL:</span> <a href="${this.escapeHtml(input.url)}" target="_blank" class="link link-primary text-xs">${this.escapeHtml(input.url)}</a></div>`)
      if (input.prompt) {
        formattedLines.push(`<div class="text-xs opacity-75">Prompt: ${this.escapeHtml(input.prompt)}</div>`)
      }
    }
    
    // Handle prompts/descriptions (Task tool)
    if (input.prompt && !input.url) {
      const promptPreview = input.prompt.length > 100 ? 
        input.prompt.substring(0, 100) + '...' : 
        input.prompt
      formattedLines.push(`<div class="text-xs opacity-75">Prompt: ${this.escapeHtml(promptPreview)}</div>`)
    }
    
    // If we haven't formatted anything specific, show the most important fields
    if (formattedLines.length === 0) {
      const importantFields = ['description', 'query', 'message', 'text', 'name', 'title']
      for (const field of importantFields) {
        if (input[field]) {
          const value = String(input[field])
          const preview = value.length > 100 ? value.substring(0, 100) + '...' : value
          formattedLines.push(`<div class="text-xs"><span class="font-medium capitalize">${field}:</span> ${this.escapeHtml(preview)}</div>`)
          break
        }
      }
    }
    
    // If still nothing, fall back to compact JSON
    if (formattedLines.length === 0) {
      // Show only the most relevant fields in a compact format
      const relevantFields = Object.keys(input).filter(key => 
        !['timeout', 'debug', 'verbose', 'force'].includes(key)
      ).slice(0, 3)
      
      if (relevantFields.length > 0) {
        const compactObj = {}
        relevantFields.forEach(key => compactObj[key] = input[key])
        return `<code class="text-xs">${this.escapeHtml(JSON.stringify(compactObj, null, 2))}</code>`
      }
    }
    
    return formattedLines.join('')
  }
  
  formatTodoList(todos) {
    if (!todos || !Array.isArray(todos)) {
      return ''
    }
    
    let todoHtml = '<div class="todo-list space-y-1 mt-2">'
    
    todos.forEach((todo, index) => {
      const statusIcon = this.getTodoStatusIcon(todo.status)
      const statusClass = this.getTodoStatusClass(todo.status)
      const isChecked = todo.status === 'completed' ? 'checked' : ''
      
      todoHtml += `
        <div class="flex items-start gap-2 p-2 rounded ${statusClass}">
          <input type="checkbox" class="checkbox checkbox-sm mt-0.5" ${isChecked} disabled />
          <div class="flex-1">
            <span class="text-sm ${todo.status === 'completed' ? 'line-through opacity-60' : ''}">${this.escapeHtml(todo.content)}</span>
            <span class="ml-2 badge badge-sm ${this.getTodoBadgeClass(todo.status)}">${statusIcon} ${todo.status}</span>
          </div>
        </div>
      `
    })
    
    todoHtml += '</div>'
    
    // Add summary
    const completed = todos.filter(t => t.status === 'completed').length
    const inProgress = todos.filter(t => t.status === 'in_progress').length
    const pending = todos.filter(t => t.status === 'pending').length
    
    todoHtml += `
      <div class="text-xs mt-2 opacity-70">
        Summary: ${completed} completed, ${inProgress} in progress, ${pending} pending
      </div>
    `
    
    return todoHtml
  }
  
  getTodoStatusIcon(status) {
    switch(status) {
      case 'completed': return '✅'
      case 'in_progress': return '🔄'
      case 'pending': return '⏳'
      default: return '📝'
    }
  }
  
  getTodoStatusClass(status) {
    switch(status) {
      case 'completed': return 'bg-success/10'
      case 'in_progress': return 'bg-warning/10'
      case 'pending': return 'bg-base-200'
      default: return 'bg-base-200'
    }
  }
  
  getTodoBadgeClass(status) {
    switch(status) {
      case 'completed': return 'badge-success'
      case 'in_progress': return 'badge-warning'
      case 'pending': return 'badge-ghost'
      default: return 'badge-ghost'
    }
  }
  
  formatSystemMessageContent(content) {
    if (!content || typeof content !== 'string') {
      return this.escapeHtml(String(content || ''))
    }
    
    // Clean up the content string
    const cleanContent = content.toString().trim()
    
    // Check if this is a system init message and return friendly message
    if (cleanContent.includes('"type":"system"') && cleanContent.includes('"subtype":"init"')) {
      return '🤔 AllSpark Bot is thinking about your message...'
    }
    
    // Check if the content looks like JSON
    if (this.isJsonString(cleanContent)) {
      try {
        const parsed = JSON.parse(cleanContent)
        // Check parsed JSON for system init
        if (parsed.type === 'system' && parsed.subtype === 'init') {
          return '🤔 AllSpark Bot is thinking about your message...'
        }
        return this.convertJsonToMarkdown(parsed)
      } catch (e) {
        console.log('Failed to parse JSON in system message:', e)
        // Fall back to regular formatting
        return this.escapeHtml(cleanContent)
      }
    }
    
    // Check if content contains JSON-like structures within text
    const jsonMatches = cleanContent.match(/\{[\s\S]*?\}/g)
    if (jsonMatches && jsonMatches.length > 0) {
      let formattedContent = cleanContent
      
      jsonMatches.forEach(match => {
        try {
          const parsed = JSON.parse(match)
          const markdownJson = this.convertJsonToMarkdown(parsed)
          formattedContent = formattedContent.replace(match, markdownJson)
        } catch (e) {
          // Keep original if parsing fails
        }
      })
      
      return formattedContent
    }
    
    // No JSON detected, return escaped HTML
    return this.escapeHtml(cleanContent)
  }
  
  isJsonString(str) {
    const trimmed = str.trim()
    return (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
           (trimmed.startsWith('[') && trimmed.endsWith(']'))
  }
  
  convertJsonToMarkdown(obj) {
    if (typeof obj === 'string') {
      return this.escapeHtml(obj)
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '`[]` (empty array)'
      }
      
      let markdown = '**Array:**\n\n'
      obj.forEach((item, index) => {
        markdown += `${index + 1}. ${this.convertJsonToMarkdown(item)}\n`
      })
      return markdown
    }
    
    if (typeof obj === 'object' && obj !== null) {
      if (Object.keys(obj).length === 0) {
        return '`{}` (empty object)'
      }
      
      let markdown = '**Session Configuration:**\n\n'
      Object.entries(obj).forEach(([key, value]) => {
        // Make key names more readable
        const readableKey = key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
        
        markdown += `- **${readableKey}**: `
        
        if (typeof value === 'string' && (value.includes('\n') || value.length > 60)) {
          // Multi-line or long strings get code blocks
          markdown += '\n\n```\n' + value + '\n```\n\n'
        } else if (typeof value === 'object' && value !== null) {
          // Nested objects get indented JSON
          markdown += '\n\n```json\n' + JSON.stringify(value, null, 2) + '\n```\n\n'
        } else {
          // Simple values inline
          markdown += `\`${this.escapeHtml(String(value))}\`\n`
        }
      })
      return markdown
    }
    
    // Primitive values
    return `\`${this.escapeHtml(String(obj))}\``
  }
  
  renderSystemMessage(messageData) {
    // Temporarily add a placeholder while we fetch the rendered message
    const placeholderId = `placeholder-${messageData.id}`
    const placeholderHtml = `
      <div id="${placeholderId}" class="alert alert-info mb-2">
        <div class="flex items-start gap-3">
          <i class="fas fa-info-circle text-lg mt-1"></i>
          <div class="flex-1">
            <span class="loading loading-dots loading-sm"></span>
          </div>
        </div>
      </div>
    `
    this.safeAppendToMessages(placeholderHtml)
    this.scrollToBottom()
    
    // Fetch the properly rendered message from the server
    const sessionId = this.sessionIdValue
    const url = `/claude_code/sessions/${sessionId}/render_message`
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({ message: messageData })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.html) {
        // Replace the placeholder with the properly rendered message
        const placeholder = document.getElementById(placeholderId)
        if (placeholder) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = data.html
          placeholder.replaceWith(tempDiv.firstElementChild)
          this.scrollToBottom()
        }
      } else {
        // Fallback: just show the text content
        const placeholder = document.getElementById(placeholderId)
        if (placeholder) {
          placeholder.querySelector('.flex-1').innerHTML = `
            <div class="prose prose-sm max-w-none">
              ${this.escapeHtml(messageData.content)}
            </div>
          `
        }
      }
    })
    .catch(error => {
      console.error('Failed to render system message:', error)
      // Fallback: just show the escaped content
      const placeholder = document.getElementById(placeholderId)
      if (placeholder) {
        placeholder.querySelector('.flex-1').innerHTML = `
          <div class="prose prose-sm max-w-none">
            ${this.escapeHtml(messageData.content)}
          </div>
        `
      }
    })
  }
  
  renderAssistantMessage(messageData) {
    // Temporarily add a placeholder while we fetch the rendered message
    const placeholderId = `placeholder-${messageData.id}`
    const placeholderHtml = `
      <div id="${placeholderId}" class="chat chat-start mb-2">
        <div class="chat-image avatar">
          <div class="w-10 rounded-full bg-base-300 p-1">
            <img src="/logo.png" alt="AllSpark" class="w-full h-full object-contain" />
          </div>
        </div>
        <div class="chat-header">
          AllSpark Bot
          <time class="text-xs opacity-50">${new Date().toLocaleTimeString()}</time>
        </div>
        <div class="chat-bubble">
          <span class="loading loading-dots loading-sm"></span>
        </div>
      </div>
    `
    this.safeAppendToMessages(placeholderHtml)
    this.scrollToBottom()
    
    // Fetch the properly rendered message from the server
    const sessionId = this.sessionIdValue
    const url = `/claude_code/sessions/${sessionId}/render_message`
    
    // Add a timeout to prevent hanging
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      },
      body: JSON.stringify({ message: messageData }),
      signal: controller.signal
    })
    .then(response => {
      clearTimeout(timeoutId)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.html) {
        // Replace the placeholder with the properly rendered message
        const placeholder = document.getElementById(placeholderId)
        if (placeholder) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = data.html
          placeholder.replaceWith(tempDiv.firstElementChild)
          this.scrollToBottom()
        }
      } else {
        // Fallback: render markdown client-side
        this.renderMarkdownFallback(placeholderId, messageData)
      }
    })
    .catch(error => {
      clearTimeout(timeoutId)
      console.error('Failed to render assistant message:', error)
      // Fallback: render markdown client-side
      this.renderMarkdownFallback(placeholderId, messageData)
    })
  }
  
  renderMarkdownFallback(placeholderId, messageData) {
    const placeholder = document.getElementById(placeholderId)
    if (placeholder) {
      const bubble = placeholder.querySelector('.chat-bubble')
      if (bubble) {
        // Use a simple client-side markdown rendering approach
        bubble.innerHTML = `
          <div class="prose prose-sm max-w-none">
            ${this.simpleMarkdownToHtml(messageData.content)}
          </div>
        `
      }
    }
  }
  
  simpleMarkdownToHtml(text) {
    if (!text) return ''
    
    // Escape HTML first
    let html = this.escapeHtml(text)
    
    // Convert code blocks
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
      return `<pre><code class="language-${lang || 'plaintext'}">${code.trim()}</code></pre>`
    })
    
    // Convert inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
    
    // Convert bold
    html = html.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>')
    
    // Convert italic
    html = html.replace(/\*([^\*]+)\*/g, '<em>$1</em>')
    
    // Convert headings
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')
    
    // Convert lists
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>')
    html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => {
      return `<ul>${match}</ul>`
    })
    
    // Convert numbered lists
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    
    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="link link-primary">$1</a>')
    
    // Convert line breaks
    html = html.replace(/\n\n/g, '</p><p>')
    html = html.replace(/\n/g, '<br>')
    
    // Wrap in paragraphs if needed
    if (!html.startsWith('<')) {
      html = `<p>${html}</p>`
    }
    
    return html
  }
  
  appendSystemMessageStreaming(content) {
    // Skip system messages with empty or problematic content
    if (!content || content === 'undefined' || content === 'null' || content === 'undefined: null' || content === null || content.toString().trim() === '') {
      console.log('Skipping system message with empty/invalid content:', { content })
      return
    }
    
    // Check if this is a system init message and replace with friendly message
    const contentStr = content.toString()
    let processedContent = content
    
    // Check for system init JSON pattern
    if (contentStr.includes('"type":"system"') && contentStr.includes('"subtype":"init"')) {
      // Replace the entire technical JSON with a friendly message
      processedContent = '🤔 AllSpark Bot is thinking about your message...'
    } else if (this.isJsonString(contentStr)) {
      // Check if it's a parsed JSON object
      try {
        const parsed = JSON.parse(contentStr)
        if (parsed.type === 'system' && parsed.subtype === 'init') {
          processedContent = '🤔 AllSpark Bot is thinking about your message...'
        }
      } catch (e) {
        // Not valid JSON, use original content
      }
    }
    
    // Render the message directly like tool use messages
    const messageId = `system-${Date.now()}`
    const timestamp = new Date().toLocaleTimeString()
    
    const systemHtml = `
      <div class="chat chat-start mb-2" data-message-id="${messageId}">
        <div class="chat-image avatar">
          <div class="w-10 rounded-full bg-secondary text-secondary-content">
            <div class="text-xl flex items-center justify-center h-full">
              <i class="fas fa-info-circle"></i>
            </div>
          </div>
        </div>
        <div class="chat-header">
          System
          <time class="text-xs opacity-50">${timestamp}</time>
        </div>
        <div class="chat-bubble chat-bubble-info">
          <div class="prose prose-sm max-w-none">
            ${this.simpleMarkdownToHtml(processedContent)}
          </div>
        </div>
      </div>
    `
    
    if (this.safeAppendToMessages(systemHtml)) {
      this.scrollToBottom()
    }
  }
  
  appendAssistantMessageStreaming(content) {
    // Skip messages with empty or problematic content
    if (!content || content === 'undefined' || content === 'null' || content === 'undefined: null' || content === null || content.toString().trim() === '') {
      console.log('Skipping assistant message with empty/invalid content:', { content })
      return
    }
    
    // Render the message directly like tool use messages
    const messageId = `assistant-${Date.now()}`
    const timestamp = new Date().toLocaleTimeString()
    
    const assistantHtml = `
      <div class="chat chat-start mb-2" data-message-id="${messageId}">
        <div class="chat-image avatar">
          <div class="w-10 rounded-full bg-base-300 p-1">
            <img src="/logo.png" alt="AllSpark" class="w-full h-full object-contain" />
          </div>
        </div>
        <div class="chat-header">
          AllSpark Bot
          <time class="text-xs opacity-50">${timestamp}</time>
        </div>
        <div class="chat-bubble">
          <div class="prose prose-sm max-w-none">
            ${this.simpleMarkdownToHtml(content)}
          </div>
        </div>
      </div>
    `
    
    if (this.safeAppendToMessages(assistantHtml)) {
      this.scrollToBottom()
    }
  }
  
  finalizeAllStreamingMessages() {
    // Check if messages target exists
    if (!this.hasMessagesTarget) {
      return
    }
    // Find all streaming messages and finalize them
    const streamingMessages = this.messagesTarget.querySelectorAll('.streaming-message')
    
    streamingMessages.forEach(streamingMsg => {
      // Remove streaming class and loading indicator
      streamingMsg.classList.remove('streaming-message')
      const loadingIndicator = streamingMsg.querySelector('.loading')
      if (loadingIndicator) {
        loadingIndicator.remove()
      }
      
      // Convert content to final markdown rendering
      const contentSpan = streamingMsg.querySelector('.streaming-content')
      if (contentSpan && contentSpan.textContent.trim()) {
        // Get the message content but don't render it here
        // The streaming message should already be properly rendered
        console.log('Finalizing streaming message, keeping existing rendered content')
      }
    })
    
    console.log('All streaming messages finalized')
  }
  
  reRenderLastMessage() {
    // This method is kept for backward compatibility but now just finalizes streaming
    this.finalizeAllStreamingMessages()
  }
  
  // Handle Enter key to send message
  handleEnter(event) {
    // This is only called when Enter is pressed thanks to keydown.enter modifier
    if (!event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }
  
  // Explicitly handle paste to ensure it works
  handlePaste(event) {
    // Allow paste - don't prevent default
    // Removed console.log for performance
    // The default behavior will handle the paste
  }
  
  // Handle timeline item click from custom event
  handleTimelineItemClick(event) {
    const itemData = event.detail
    this.showTimelineItemDetailsFromData(itemData)
  }
  
  // Show timeline item details in modal
  showTimelineItemDetails(event) {
    const itemData = JSON.parse(event.currentTarget.dataset.itemData)
    this.showTimelineItemDetailsFromData(itemData)
  }
  
  // Show timeline item details from data
  showTimelineItemDetailsFromData(itemData) {
    if (!this.hasTimelineItemModalTarget || !this.hasTimelineItemModalContentTarget) {
      console.error('Timeline modal targets not found')
      return
    }
    
    // Build modal content based on item type
    let modalContent = `
      <h3 class="font-bold text-lg mb-4">
        <i class="fas ${itemData.icon} mr-2 text-${itemData.color}"></i>
        ${itemData.title}
      </h3>
    `
    
    // Basic info
    modalContent += `
      <div class="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div class="text-sm text-base-content/70">Type</div>
          <div class="font-semibold">${itemData.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
        </div>
        <div>
          <div class="text-sm text-base-content/70">Time</div>
          <div class="font-semibold">${new Date(itemData.timestamp).toLocaleString()}</div>
        </div>
      </div>
    `
    
    // Description
    if (itemData.description) {
      modalContent += `
        <div class="mb-4">
          <div class="text-sm text-base-content/70 mb-1">Description</div>
          <div class="bg-base-200 p-3 rounded">${this.escapeHtml(itemData.description)}</div>
        </div>
      `
    }
    
    // Type-specific details
    if (itemData.data) {
      switch (itemData.type) {
        case 'command':
          modalContent += this.buildCommandDetails(itemData.data)
          break
        case 'file_operation':
          modalContent += this.buildFileOperationDetails(itemData.data)
          break
        case 'message':
          modalContent += this.buildMessageDetails(itemData.data)
          break
      }
    }
    
    this.timelineItemModalContentTarget.innerHTML = modalContent
    this.timelineItemModalTarget.showModal()
  }
  
  buildCommandDetails(data) {
    let html = '<div class="space-y-4">'
    
    // Model info
    html += `
      <div class="alert alert-info mb-4">
        <i class="fas fa-database mr-2"></i>
        <span class="font-semibold">Model: ClaudeCodeCommand</span>
      </div>
    `
    
    // All attributes in a table
    html += `
      <div>
        <div class="text-sm text-base-content/70 mb-2">All Attributes</div>
        <div class="overflow-x-auto">
          <table class="table table-zebra table-sm">
            <tbody>
    `
    
    // Display all attributes
    const attributes = [
      { key: 'id', label: 'ID' },
      { key: 'claude_code_session_id', label: 'Session ID' },
      { key: 'user_id', label: 'User ID' },
      { key: 'prompt', label: 'Prompt' },
      { key: 'response_content', label: 'Response' },
      { key: 'thinking_content', label: 'Thinking Process' },
      { key: 'context_mode', label: 'Context Mode' },
      { key: 'status', label: 'Status' },
      { key: 'tool_usage', label: 'Tool Usage' },
      { key: 'token_usage', label: 'Token Usage' },
      { key: 'file_changes', label: 'File Changes' },
      { key: 'cost', label: 'Cost' },
      { key: 'error_details', label: 'Error Details' },
      { key: 'response_metadata', label: 'Response Metadata' },
      { key: 'created_at', label: 'Created At' },
      { key: 'updated_at', label: 'Updated At' },
      { key: 'completed_at', label: 'Completed At' }
    ]
    
    attributes.forEach(attr => {
      const value = data[attr.key]
      if (value !== undefined && value !== null && value !== '' && (typeof value !== 'object' || Object.keys(value).length > 0)) {
        let displayValue = ''
        
        // Special formatting for specific fields
        switch(attr.key) {
          case 'response_content':
          case 'thinking_content':
          case 'prompt':
            displayValue = `<div class="max-h-64 overflow-y-auto"><pre class="whitespace-pre-wrap text-xs">${this.escapeHtml(value)}</pre></div>`
            break
          case 'cost':
            displayValue = value ? `$${parseFloat(value).toFixed(6)}` : 'N/A'
            break
          case 'tool_usage':
            if (Array.isArray(value) && value.length > 0) {
              displayValue = `<div class="space-y-2">`
              value.forEach(tool => {
                displayValue += `
                  <div class="bg-base-200 p-2 rounded">
                    <div class="font-semibold text-xs">${tool.name}</div>
                    <div class="text-xs opacity-70">ID: ${tool.id}</div>
                    ${tool.result ? `<div class="text-xs mt-1">Result: ${tool.result.is_error ? '❌ Error' : '✅ Success'}</div>` : ''}
                  </div>
                `
              })
              displayValue += `</div>`
            } else {
              displayValue = 'None'
            }
            break
          case 'token_usage':
            if (value && typeof value === 'object') {
              displayValue = `
                <div class="grid grid-cols-2 gap-2 text-xs">
                  <div>Input: ${value.input_tokens || 0}</div>
                  <div>Output: ${value.output_tokens || 0}</div>
                  <div>Cache Read: ${value.cache_read_tokens || 0}</div>
                  <div>Total: ${value.total_tokens || 0}</div>
                </div>
              `
            } else {
              displayValue = 'N/A'
            }
            break
          case 'file_changes':
            if (value && Object.keys(value).length > 0) {
              displayValue = `<div class="space-y-1">`
              Object.entries(value).forEach(([path, changes]) => {
                displayValue += `<div class="text-xs"><span class="font-mono">${path}</span>: ${changes.length} changes</div>`
              })
              displayValue += `</div>`
            } else {
              displayValue = 'No files changed'
            }
            break
          default:
            displayValue = typeof value === 'object' ? 
              `<pre class="text-xs max-h-32 overflow-y-auto">${JSON.stringify(value, null, 2)}</pre>` : 
              this.escapeHtml(String(value))
        }
        
        html += `
          <tr>
            <td class="font-semibold align-top">${attr.label}</td>
            <td>${displayValue}</td>
          </tr>
        `
      }
    })
    
    html += `
            </tbody>
          </table>
        </div>
      </div>
    `
    
    // Timing info highlight
    if (data.created_at && data.completed_at) {
      const duration = new Date(data.completed_at) - new Date(data.created_at)
      html += `
        <div class="stats shadow">
          <div class="stat">
            <div class="stat-title">Duration</div>
            <div class="stat-value text-primary">${Math.round(duration / 1000)}s</div>
          </div>
          <div class="stat">
            <div class="stat-title">Status</div>
            <div class="stat-value text-${this.getStatusColor(data.status || 'unknown')}">${data.status || 'Unknown'}</div>
          </div>
        </div>
      `
    }
    
    html += '</div>'
    return html
  }
  
  buildFileOperationDetails(data) {
    let html = '<div class="space-y-4">'
    
    // Model info
    html += `
      <div class="alert alert-info mb-4">
        <i class="fas fa-database mr-2"></i>
        <span class="font-semibold">Data Type: File Operation (JSONB)</span>
      </div>
    `
    
    // All attributes in a table
    html += `
      <div>
        <div class="text-sm text-base-content/70 mb-2">All Attributes</div>
        <div class="overflow-x-auto">
          <table class="table table-zebra table-sm">
            <tbody>
    `
    
    // Display all attributes dynamically
    Object.entries(data).forEach(([key, value]) => {
      html += `
        <tr>
          <td class="font-semibold">${key}</td>
          <td>
            ${typeof value === 'object' ? 
              `<pre class="text-xs">${JSON.stringify(value, null, 2)}</pre>` : 
              this.escapeHtml(String(value))
            }
          </td>
        </tr>
      `
    })
    
    html += `
            </tbody>
          </table>
        </div>
      </div>
    `
    
    // Content changes preview if available
    if (data.content_before || data.content_after) {
      html += `
        <div>
          <div class="text-sm text-base-content/70 mb-1">Content Changes</div>
          <div class="tabs tabs-boxed">
            <a class="tab tab-active" onclick="this.parentElement.querySelectorAll('.tab').forEach(t => t.classList.remove('tab-active')); this.classList.add('tab-active'); document.getElementById('before-content').classList.remove('hidden'); document.getElementById('after-content').classList.add('hidden');">Before</a>
            <a class="tab" onclick="this.parentElement.querySelectorAll('.tab').forEach(t => t.classList.remove('tab-active')); this.classList.add('tab-active'); document.getElementById('before-content').classList.add('hidden'); document.getElementById('after-content').classList.remove('hidden');">After</a>
          </div>
          <div id="before-content" class="bg-base-200 p-3 rounded mt-2 max-h-64 overflow-y-auto">
            <pre class="text-sm">${this.escapeHtml(data.content_before || '(empty)')}</pre>
          </div>
          <div id="after-content" class="bg-base-200 p-3 rounded mt-2 max-h-64 overflow-y-auto hidden">
            <pre class="text-sm">${this.escapeHtml(data.content_after || '(empty)')}</pre>
          </div>
        </div>
      `
    }
    
    html += '</div>'
    return html
  }
  
  buildMessageDetails(data) {
    let html = '<div class="space-y-4">'
    
    // Model info
    html += `
      <div class="alert alert-info mb-4">
        <i class="fas fa-database mr-2"></i>
        <span class="font-semibold">Data Type: Message (JSONB)</span>
      </div>
    `
    
    // All attributes in a table
    html += `
      <div>
        <div class="text-sm text-base-content/70 mb-2">All Attributes</div>
        <div class="overflow-x-auto">
          <table class="table table-zebra table-sm">
            <tbody>
    `
    
    // Display all attributes dynamically
    Object.entries(data).forEach(([key, value]) => {
      html += `
        <tr>
          <td class="font-semibold">${key}</td>
          <td>
            ${key === 'content' && typeof value === 'string' && value.length > 100 ?
              `<div class="max-h-32 overflow-y-auto"><pre class="whitespace-pre-wrap text-xs">${this.escapeHtml(value)}</pre></div>` :
              typeof value === 'object' ? 
                `<pre class="text-xs">${JSON.stringify(value, null, 2)}</pre>` : 
                this.escapeHtml(String(value))
            }
          </td>
        </tr>
      `
    })
    
    html += `
            </tbody>
          </table>
        </div>
      </div>
    `
    
    // Highlight key info
    html += `
      <div class="stats shadow">
        <div class="stat">
          <div class="stat-title">Role</div>
          <div class="stat-value text-sm">${data.role || 'Unknown'}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Type</div>
          <div class="stat-value text-sm">${data.type || 'message'}</div>
        </div>
      </div>
    `
    
    html += '</div>'
    return html
  }
  
  getStatusColor(status) {
    switch (status) {
      case 'success':
      case 'completed':
        return 'success'
      case 'failed':
      case 'error':
        return 'error'
      case 'pending':
      case 'running':
        return 'warning'
      default:
        return 'neutral'
    }
  }
  
  // Insert prompt from quick actions
  insertPrompt(event) {
    const prompt = event.currentTarget.dataset.prompt
    if (prompt && this.hasInputTarget) {
      this.inputTarget.value = prompt
      this.inputTarget.focus()
      // Auto resize if the textarea has that capability
      if (typeof this.autoResize === 'function') {
        this.autoResize()
      }
    }
  }
  
  // Fetch session settings and update UI
  fetchSessionSettings() {
    if (!this.sessionIdValue) {
      console.warn('No session ID available, skipping settings fetch')
      return
    }
    
    console.log('Fetching session settings for:', this.sessionIdValue)
    
    fetch(`/claude_code/sessions/${this.sessionIdValue}.json`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch settings: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        console.log('Session data received:', data)
        // Update auto-apply checkbox if it exists
        if (this.hasAutoApplyToggleTarget) {
          console.log('Setting checkbox to:', data.auto_apply_safe_changes)
          this.autoApplyToggleTarget.checked = data.auto_apply_safe_changes
        } else {
          console.warn('Auto-apply toggle target not found')
        }
        console.log('Session settings loaded, auto_apply_safe_changes:', data.auto_apply_safe_changes)
      })
      .catch(error => {
        console.error('Error fetching session settings:', error)
      })
  }
  
  // Handle auto-apply setting change (called by data-action)
  updateAutoApplySetting(event) {
    const isChecked = event.target.checked
    console.log('Updating auto-apply setting to:', isChecked)
    
    // Update session via AJAX
    fetch(`/claude_code/sessions/${this.sessionIdValue}/update_setting`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        setting: 'auto_apply_safe_changes',
        value: isChecked
      })
    })
    .then(response => {
      console.log('Response status:', response.status)
      if (!response.ok) {
        return response.text().then(text => {
          console.error('Response body:', text)
          throw new Error(`Failed to update setting: ${response.status} - ${text}`)
        })
      }
      return response.json()
    })
    .then(data => {
      console.log('Auto-apply setting updated successfully:', data)
      console.log('Database value is now:', data.value)
      // Update the checkbox to reflect the actual database value
      event.target.checked = data.value
    })
    .catch(error => {
      console.error('Error updating auto-apply setting:', error)
      alert(`Failed to update auto-apply setting: ${error.message}`)
      // Revert checkbox on error
      event.target.checked = !isChecked
    })
  }
  
  // Toggle auto-apply setting (legacy method for compatibility)
  toggleAutoApply(event) {
    this.updateAutoApplySetting(event)
  }
  
  // Pause session
  pauseSession() {
    console.log('Pausing session...')
    
    if (this.channel) {
      this.channel.perform('pause_session')
    }
  }
  
  // Resume session
  resumeSession() {
    console.log('Resuming session...')
    
    if (this.channel) {
      this.channel.perform('resume_session')
    }
  }
  
  // Update session state UI
  updateSessionState(state) {
    console.log('Updating session state UI:', state)
    
    // Store current state
    this.currentSessionState = state
    
    // Toggle pause/resume buttons based on state
    if (state === 'paused') {
      if (this.hasPauseButtonTarget) {
        this.pauseButtonTarget.classList.add('hidden')
      }
      if (this.hasResumeButtonTarget) {
        this.resumeButtonTarget.classList.remove('hidden')
      }
      
      // Show paused indicator
      this.appendSystemMessage('Session paused. Click Resume to continue.')
    } else {
      if (this.hasPauseButtonTarget) {
        this.pauseButtonTarget.classList.remove('hidden')
      }
      if (this.hasResumeButtonTarget) {
        this.resumeButtonTarget.classList.add('hidden')
      }
      
      if (state === 'active' || state === 'idle') {
        // Don't show "Session resumed" message - it's not needed
      }
    }
    
    // Update session state display if exists
    const stateDisplay = this.element.querySelector('[data-session-state]')
    if (stateDisplay) {
      stateDisplay.textContent = state.charAt(0).toUpperCase() + state.slice(1)
      stateDisplay.className = `text-sm text-base-content/70 session-state-${state}`
    }
  }
  
  updateActionButtons(state) {
    console.log('Updating action buttons for state:', state)
    
    // Find all action buttons
    const endButton = this.element.querySelector('[data-action*="endSession"]')
    const pauseButton = this.element.querySelector('[data-action*="pauseSession"]')
    const resumeButton = this.element.querySelector('[data-action*="resumeSession"]')
    const restartButton = this.element.querySelector('[data-action*="restartSession"]')
    const stopWorkersButton = this.element.querySelector('[data-action*="stopWorkers"]')
    
    if (state === 'completed') {
      // Session is completed - show restart, hide others
      if (endButton) endButton.classList.add('hidden')
      if (pauseButton) pauseButton.classList.add('hidden')
      if (resumeButton) resumeButton.classList.add('hidden')
      if (stopWorkersButton) stopWorkersButton.classList.add('hidden')
      if (restartButton) {
        restartButton.classList.remove('hidden')
        restartButton.classList.add('btn-primary')
      }
    } else if (state === 'paused') {
      // Session is paused - show resume
      if (pauseButton) pauseButton.classList.add('hidden')
      if (resumeButton) resumeButton.classList.remove('hidden')
      if (restartButton) restartButton.classList.add('hidden')
    } else {
      // Session is active/idle - show normal buttons
      if (endButton) endButton.classList.remove('hidden')
      if (pauseButton) pauseButton.classList.remove('hidden')
      if (resumeButton) resumeButton.classList.add('hidden')
      if (stopWorkersButton) stopWorkersButton.classList.remove('hidden')
      if (restartButton) restartButton.classList.add('hidden')
    }
  }
  
  // Stop all workers for this session
  stopWorkers() {
    if (confirm('This will stop all running background jobs for this session. Continue?')) {
      console.log('Stopping all workers...')
      
      if (this.channel) {
        this.channel.perform('stop_all_workers')
        this.appendSystemMessage('Stopping all background workers...')
      } else {
        this.showError('Channel not connected')
      }
    }
  }
  
  // End/Stop session via WebSocket
  endSession() {
    if (confirm('Are you sure you want to end this session? This will stop all running tasks and mark the session as completed.')) {
      console.log('Ending session...')
      
      if (this.channel) {
        this.channel.perform('end_session')
        this.appendSystemMessage('Ending session...')
      } else {
        this.showError('Channel not connected')
      }
    }
  }

  // Restart session via WebSocket
  restartSession(event) {
    if (event) event.preventDefault()
    
    if (confirm('This will clear all conversation history and restart the session. Continue?')) {
      console.log('Restarting session...')
      
      if (this.channel) {
        this.channel.perform('restart_session')
        this.appendSystemMessage('Restarting session...')
      } else {
        this.showError('Channel not connected')
      }
    }
  }

  // Handle slash commands for authentication and other system commands
  handleSlashCommand(command) {
    const [cmd, ...args] = command.slice(1).split(' ')
    
    // Clear input
    this.inputTarget.value = ''
    
    // Show the command that was typed
    this.appendMessage('user', command)
    
    switch (cmd.toLowerCase()) {
      case 'login':
        this.handleLoginCommand()
        break
      case 'logout':
        this.handleLogoutCommand()
        break
      case 'auth':
        this.handleAuthCommand(args)
        break
      case 'switch':
        if (args[0] === 'auth' || args[0] === 'authentication') {
          this.handleSwitchAuthCommand(args[1])
        } else {
          this.showError('Unknown switch command. Use: /switch auth [api|session]')
        }
        break
      case 'status':
        this.handleStatusCommand()
        break
      case 'help':
        this.handleHelpCommand()
        break
      default:
        // Forward unknown commands to Claude Code CLI
        this.forwardSlashCommand(command)
    }
  }

  async handleLoginCommand() {
    try {
      this.appendSystemMessage('🔐 Initiating authentication with Claude.ai account...')
      
      const response = await fetch(`/claude_code/sessions/${this.sessionIdValue}/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ auth_type: 'session' })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.appendSystemMessage(`✅ ${data.message || 'Authentication successful! You can now use your Pro/Max account.'}`)
      } else {
        this.appendSystemMessage('❌ Authentication failed. Please try again or use API key authentication.')
      }
    } catch (error) {
      console.error('Login command error:', error)
      this.appendSystemMessage('❌ Login failed due to network error. Please try again.')
    }
  }

  async handleAuthCommand(args) {
    if (args.length === 0) {
      // Show current auth status
      try {
        const response = await fetch(`/claude_code/sessions/${this.sessionIdValue}/auth_status`)
        const data = await response.json()
        
        const statusMessage = `
**Authentication Status:**
- **Current Method**: ${data.method || 'unknown'}
- **API Key Available**: ${data.api_key_available ? '✅' : '❌'}
- **Session Authenticated**: ${data.session_authenticated ? '✅' : '❌'}
- **Can Switch**: ${data.can_switch ? '✅' : '❌'}

**Available Commands:**
- \`/login\` - Authenticate with Claude.ai account
- \`/auth switch api\` - Switch to API key authentication
- \`/auth switch session\` - Switch to session authentication
        `
        this.appendSystemMessage(statusMessage)
      } catch (error) {
        this.appendSystemMessage('❌ Failed to get authentication status')
      }
    } else if (args[0] === 'switch') {
      this.handleSwitchAuthCommand(args[1])
    }
  }

  async handleSwitchAuthCommand(method) {
    try {
      const response = await fetch(`/claude_code/sessions/${this.sessionIdValue}/switch_auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ method: method })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.appendSystemMessage(`✅ ${data.message}`)
      } else {
        this.appendSystemMessage(`❌ Failed to switch authentication method`)
      }
    } catch (error) {
      this.appendSystemMessage('❌ Switch failed due to network error')
    }
  }

  async handleLogoutCommand() {
    try {
      this.appendSystemMessage('🔓 Clearing authentication session...')
      
      const response = await fetch(`/claude_code/sessions/${this.sessionIdValue}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        this.appendSystemMessage('✅ Authentication cleared. You can now use /login to authenticate again.')
      } else {
        this.appendSystemMessage('❌ Failed to clear authentication')
      }
    } catch (error) {
      this.appendSystemMessage('❌ Logout failed due to network error')
    }
  }

  async handleStatusCommand() {
    try {
      const response = await fetch(`/claude_code/sessions/${this.sessionIdValue}/status`)
      const data = await response.json()
      
      const statusMessage = `
**Session Status:**
- **ID**: ${data.session_id || 'unknown'}
- **State**: ${data.state || 'unknown'}
- **Authentication**: ${data.auth_method || 'unknown'}
- **Messages**: ${data.message_count || 0}
- **Started**: ${data.started_at || 'unknown'}

**Claude Code CLI:**
- **Available**: ${data.cli_available ? '✅' : '❌'}
- **Version**: ${data.cli_version || 'unknown'}
      `
      this.appendSystemMessage(statusMessage)
    } catch (error) {
      this.appendSystemMessage('❌ Failed to get session status')
    }
  }

  handleHelpCommand() {
    const helpMessage = `
**Available Commands:**

**Authentication:**
- \`/login\` - Authenticate with Claude.ai Pro/Max account
- \`/logout\` - Clear authentication session
- \`/auth\` - Show current authentication status
- \`/auth switch api\` - Switch to API key authentication
- \`/auth switch session\` - Switch to session authentication

**System:**
- \`/status\` - Show session and system status
- \`/help\` - Show this help message

**Claude Code CLI Commands:**
Any other slash commands will be forwarded directly to Claude Code CLI.
    `
    this.appendSystemMessage(helpMessage)
  }

  async forwardSlashCommand(command) {
    // Forward command to Claude Code CLI via the normal message channel
    this.appendSystemMessage(`⚡ Forwarding command to Claude Code CLI: ${command}`)
    
    // Send command through the normal execution channel
    this.channel.perform('execute', { 
      prompt: command,
      is_slash_command: true
    })
  }
  
  // Show sidebar tab
  showSidebarTab(event) {
    event.preventDefault()
    const targetTab = event.currentTarget.dataset.tab
    
    // Update tab active states
    const tabButtons = event.currentTarget.parentElement.querySelectorAll('.tab')
    tabButtons.forEach(tab => tab.classList.remove('tab-active'))
    event.currentTarget.classList.add('tab-active')
    
    // Show/hide tab content
    this.sidebarTabTargets.forEach(tabContent => {
      const tabName = tabContent.dataset.tabName
      if (tabName === targetTab) {
        tabContent.classList.remove('hidden')
        
        // If this is the context tab, load the context data
        if (targetTab === 'context') {
          this.loadContextData()
        }
      } else {
        tabContent.classList.add('hidden')
      }
    })
  }
  
  // Timeline update methods
  updateTimeline(data) {
    if (!this.hasTimelineContentTarget) {
      console.log('Timeline content target not found')
      return
    }
    
    // Find the current timeline container and refresh it
    const currentTimelineContainer = this.timelineContentTarget
    if (!currentTimelineContainer) {
      console.log('Timeline container not found')
      return
    }
    
    // Refresh the entire timeline by fetching updated content
    fetch(`/claude_code/sessions/${this.sessionIdValue}/timeline_content`, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      // Create a temporary div to parse the response
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      
      // Find the timeline content in the response
      const newTimelineContent = tempDiv.querySelector('[data-claude-code-chat-target="timelineContent"]')
      if (newTimelineContent) {
        currentTimelineContainer.innerHTML = newTimelineContent.innerHTML
        console.log('Timeline updated with new content')
      } else {
        console.error('Timeline content not found in response')
      }
    })
    .catch(error => {
      console.error('Failed to update timeline:', error)
    })
  }
  
  addTimelineItem(timelineItem) {
    if (!this.hasTimelineContentTarget) return
    
    // Create new timeline item HTML
    const timelineHtml = this.buildTimelineItemHtml(timelineItem)
    
    // Insert at the top of the timeline (most recent first)
    const timelineContainer = this.timelineContentTarget.querySelector('.space-y-3')
    if (timelineContainer) {
      timelineContainer.insertAdjacentHTML('afterbegin', timelineHtml)
    }
  }
  
  updateTimelineItem(commandId, timelineItem) {
    if (!this.hasTimelineContentTarget) return
    
    // Find existing timeline item by command ID and update it
    const existingItem = this.timelineContentTarget.querySelector(`[data-command-id="${commandId}"]`)
    if (existingItem) {
      const newHtml = this.buildTimelineItemHtml(timelineItem)
      existingItem.outerHTML = newHtml
    } else {
      // If not found, add as new item
      this.addTimelineItem(timelineItem)
    }
  }
  
  getTimelineItemUIConfig(item) {
    // Determine UI properties based on item type and status
    const type = item.type || 'unknown'
    const status = item.status || ''
    
    // Default configuration
    let config = {
      title: 'Event',
      icon: 'fa-circle',
      color: 'neutral'
    }
    
    // Configure based on type
    switch(type) {
      case 'command':
        config.icon = 'fa-terminal'
        config.title = status === 'running' ? 'Command Running' : 
                       status === 'completed' ? 'Command Executed' : 
                       status === 'failed' ? 'Command Failed' : 'Command'
        config.color = status === 'running' ? 'info' :
                       status === 'completed' ? 'success' :
                       status === 'failed' ? 'error' : 'neutral'
        break
        
      case 'user_message':
        config.title = 'User Message'
        config.icon = 'fa-user'
        config.color = 'primary'
        break
        
      case 'assistant_message':
        config.title = 'AllSpark Bot Response'
        config.icon = 'fa-robot'
        config.color = 'secondary'
        break
        
      case 'tool_use':
        config.title = 'Tool Usage'
        config.icon = 'fa-wrench'
        config.color = 'warning'
        break
        
      case 'file_operation':
        config.title = 'File Operation'
        config.icon = 'fa-file'
        config.color = status === 'success' ? 'success' : 
                       status === 'failed' ? 'error' : 'neutral'
        break
        
      case 'error':
        config.title = 'Error'
        config.icon = 'fa-exclamation-triangle'
        config.color = 'error'
        break
        
      case 'session_start':
        config.title = 'Session Started'
        config.icon = 'fa-play'
        config.color = 'success'
        break
        
      case 'session_end':
        config.title = 'Session Ended'
        config.icon = 'fa-stop'
        config.color = 'neutral'
        break
        
      default:
        // Use any provided values as fallback
        if (item.title) config.title = item.title
        if (item.icon) config.icon = item.icon
        if (item.color) config.color = item.color
    }
    
    return config
  }
  
  buildTimelineItemHtml(item) {
    // Use created_at from the timeline item, fallback to timestamp if needed
    const dateString = item.created_at || item.timestamp || new Date().toISOString()
    const timestamp = new Date(dateString)
    
    // Check if date is valid
    let formattedTime
    if (isNaN(timestamp.getTime())) {
      console.warn('Invalid date for timeline item:', dateString, item)
      formattedTime = 'now'
    } else {
      formattedTime = timestamp.toLocaleTimeString()
    }
    
    // Determine UI properties based on item type and status
    const uiConfig = this.getTimelineItemUIConfig(item)
    
    // Map colors to actual Tailwind classes
    const colorClasses = {
      'success': 'bg-success text-success-content',
      'error': 'bg-error text-error-content',
      'warning': 'bg-warning text-warning-content',
      'info': 'bg-info text-info-content',
      'primary': 'bg-primary text-primary-content',
      'secondary': 'bg-secondary text-secondary-content',
      'accent': 'bg-accent text-accent-content',
      'neutral': 'bg-neutral text-neutral-content'
    }
    
    const badgeColorClasses = {
      'success': 'badge-success',
      'error': 'badge-error',
      'warning': 'badge-warning',
      'info': 'badge-info',
      'primary': 'badge-primary',
      'secondary': 'badge-secondary',
      'accent': 'badge-accent',
      'neutral': 'badge-neutral'
    }
    
    const iconColorClass = colorClasses[uiConfig.color] || 'bg-base-300 text-base-content'
    const badgeClass = badgeColorClasses[uiConfig.color] || 'badge-neutral'
    
    // Use UI config values
    const title = uiConfig.title
    const description = item.prompt || item.description || ''
    const icon = uiConfig.icon
    const status = item.status
    
    // Use Unicode-safe base64 encoding
    let itemJson
    try {
      itemJson = btoa(unescape(encodeURIComponent(JSON.stringify(item))))
    } catch (e) {
      console.warn('Failed to encode timeline item:', e)
      itemJson = btoa(JSON.stringify({...item, content: '[Content encoding failed]'}))
    }
    
    return `
      <div class="flex gap-3 cursor-pointer hover:bg-base-200/50 p-2 -m-2 rounded-lg transition-colors timeline-item-clickable" 
           data-timeline-item="${item.type || 'command'}" 
           data-command-id="${item.id || item.command_id || ''}"
           data-item-json="${itemJson}">
        <div class="flex-shrink-0 relative">
          <div class="w-8 h-8 rounded-full ${iconColorClass} flex items-center justify-center relative z-10">
            <i class="fas ${icon} text-xs"></i>
          </div>
        </div>
        <div class="flex-1 pb-3">
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1">
              <div class="font-semibold text-sm">${this.escapeHtml(title)}</div>
              <div class="text-xs text-base-content/70 mt-1">${this.escapeHtml(description)}</div>
            </div>
            <time class="text-xs text-base-content/60 flex-shrink-0">
              ${formattedTime}
            </time>
          </div>
          ${status ? `
            <div class="flex flex-wrap gap-2 mt-2">
              <div class="badge badge-sm ${badgeClass}">
                ${this.escapeHtml(status)}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `
  }
  
  updateContextInfo(data) {
    // Update any context information displayed in the UI
    console.log('Context updated:', data)
    
    // Update context pills if they exist
    const contextPills = this.element.querySelector('[data-claude-code-chat-target="contextPills"]')
    if (contextPills && data.files) {
      // Rebuild context pills
      contextPills.innerHTML = data.files.map(file => `
        <span class="badge badge-sm badge-outline">
          <i class="fas fa-file-code mr-1"></i>
          ${file.split('/').pop()}
        </span>
      `).join('')
    }
  }
  
  // Toggle context visibility
  toggleContext(event) {
    event.preventDefault()
    console.log('toggleContext called')
    
    // Find context-related elements to toggle
    const contextPills = this.element.querySelector('[data-claude-code-chat-target="contextPills"]')
    
    if (contextPills) {
      const isHidden = contextPills.classList.contains('hidden')
      
      if (isHidden) {
        // Show context pills
        contextPills.classList.remove('hidden')
        
        // If no active files, show a helpful message
        const activeFilesCount = contextPills.querySelectorAll('.badge').length
        if (activeFilesCount === 0) {
          contextPills.innerHTML = `
            <div class="text-sm text-base-content/60 italic">
              No files in context. Claude Code will automatically add files as you work with them.
            </div>
          `
        }
      } else {
        // Hide context pills
        contextPills.classList.add('hidden')
      }
      
      // Update button appearance
      const contextButton = event.currentTarget
      if (contextButton) {
        contextButton.classList.toggle('btn-active', !isHidden)
      }
      
      console.log('Context visibility toggled:', !isHidden ? 'hidden' : 'shown')
    } else {
      console.error('Context pills element not found')
    }
    
    // Try to find and toggle any sidebar elements
    const sidebarTabs = this.element.querySelectorAll('[data-tab-name]')
    const fileBrowser = this.element.querySelector('[data-tab-name="files"]')
    
    if (fileBrowser && sidebarTabs.length > 0) {
      const isFileBrowserHidden = fileBrowser.classList.contains('hidden')
      
      // Hide all sidebar tabs first
      sidebarTabs.forEach(tab => {
        tab.classList.add('hidden')
      })
      
      // Show files tab if it was hidden, otherwise show chat tab
      if (isFileBrowserHidden) {
        fileBrowser.classList.remove('hidden')
        // Also activate the files tab button if it exists
        const filesTabButton = this.element.querySelector('[data-tab="files"]')
        if (filesTabButton) {
          const allTabButtons = this.element.querySelectorAll('.tab')
          allTabButtons.forEach(tab => tab.classList.remove('tab-active'))
          filesTabButton.classList.add('tab-active')
        }
      } else {
        // Show chat tab instead
        const chatTab = this.element.querySelector('[data-tab-name="chat"]')
        if (chatTab) {
          chatTab.classList.remove('hidden')
          const chatTabButton = this.element.querySelector('[data-tab="chat"]')
          if (chatTabButton) {
            const allTabButtons = this.element.querySelectorAll('.tab')
            allTabButtons.forEach(tab => tab.classList.remove('tab-active'))
            chatTabButton.classList.add('tab-active')
          }
        }
      }
    }
    
    console.log('Context toggled')
  }
  
  // Load context data for the context tab
  async loadContextData() {
    console.log('Loading context data for context tab')
    
    try {
      const sessionId = this.sessionIdValue
      const response = await fetch(`/claude_code/sessions/${sessionId}/context_data`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        }
      })
      
      if (response.ok) {
        const contextData = await response.json()
        this.populateContextData(contextData)
      } else {
        console.error('Failed to fetch context data')
        this.populateContextData({
          error: 'Failed to load context data'
        })
      }
    } catch (error) {
      console.error('Error fetching context:', error)
      this.populateContextData({
        error: error.message
      })
    }
  }
  
  // Populate context data in the sidebar tab
  populateContextData(contextData) {
    // Check for error
    if (contextData.error) {
      const textareaEl = document.querySelector('[data-context-full-textarea]')
      if (textareaEl) {
        textareaEl.value = `Error loading context: ${contextData.error}`
      }
      return
    }
    
    // Populate the textarea with the full context
    const textareaEl = document.querySelector('[data-context-full-textarea]')
    if (textareaEl && contextData.full_context) {
      textareaEl.value = contextData.full_context
    }
    
    // Token count
    const tokensEl = document.querySelector('[data-context-tokens]')
    if (tokensEl && contextData.total_tokens) {
      tokensEl.textContent = contextData.total_tokens.toLocaleString()
    }
  }
  
  // Show context modal with full visibility
  async showContextModal(event) {
    event.preventDefault()
    console.log('showContextModal called')
    
    const modal = document.getElementById('claude-code-context-modal')
    if (!modal) {
      console.error('Context modal not found')
      // Fallback to old toggle behavior
      this.toggleContext(event)
      return
    }
    
    // Show the modal
    modal.showModal()
    
    // Fetch and populate context data
    try {
      const sessionId = this.sessionIdValue
      const response = await fetch(`/claude_code/sessions/${sessionId}/context_data`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        const contextData = await response.json()
        this.populateContextModal(contextData)
      } else {
        console.error('Failed to fetch context data')
        this.populateContextModal({
          error: 'Failed to load context data'
        })
      }
    } catch (error) {
      console.error('Error fetching context:', error)
      this.populateContextModal({
        error: error.message
      })
    }
  }
  
  // Populate the context modal with data
  populateContextModal(contextData) {
    // Check for error
    if (contextData.error) {
      const textareaEl = document.querySelector('[data-context-full-textarea]')
      if (textareaEl) {
        textareaEl.value = `Error loading context: ${contextData.error}`
      }
      return
    }
    
    // Populate the single textarea with the full context
    const textareaEl = document.querySelector('[data-context-full-textarea]')
    if (textareaEl && contextData.full_context) {
      textareaEl.value = contextData.full_context
    }
    
    // Token count
    const tokensEl = document.querySelector('[data-context-tokens]')
    if (tokensEl && contextData.total_tokens) {
      tokensEl.textContent = contextData.total_tokens.toLocaleString()
    }
  }
  
  // Copy context to clipboard
  copyContext(event) {
    event.preventDefault()
    const textareaEl = document.querySelector('[data-context-full-textarea]')
    if (textareaEl) {
      navigator.clipboard.writeText(textareaEl.value).then(() => {
        // Show success feedback
        const button = event.currentTarget
        const originalText = button.innerHTML
        button.innerHTML = '<i class="fas fa-check mr-2"></i> Copied!'
        button.classList.add('btn-success')
        setTimeout(() => {
          button.innerHTML = originalText
          button.classList.remove('btn-success')
        }, 2000)
      }).catch(err => {
        console.error('Failed to copy:', err)
        alert('Failed to copy context to clipboard')
      })
    }
  }
  
  // Export session data
  exportSession(event) {
    event.preventDefault()
    
    // Redirect to export endpoint
    const sessionId = this.sessionIdValue
    window.location.href = `/claude_code/sessions/${sessionId}/export`
    
    console.log('Exporting session:', sessionId)
  }
  
  // Hide review panel
  hideReviewPanel(event) {
    event.preventDefault()
    
    // Find and hide the review panel
    const reviewPanel = this.element.querySelector('[data-review-panel]')
    if (reviewPanel) {
      reviewPanel.classList.add('hidden')
    }
    
    console.log('Review panel hidden')
  }
  
  // Show interrupt button when processing
  showInterruptButton() {
    // No longer dynamically creating interrupt button
    // Button is now permanently in the header
  }
  
  // Hide interrupt button
  hideInterruptButton() {
    // No longer hiding interrupt button
    // Button is now always visible in the header
  }

  // ── Auth Error Handling ───────────────────────────────────────────────

  showAuthErrorBanner(data) {
    // Remove any existing auth banner
    const existing = document.getElementById('auth-error-banner')
    if (existing) existing.remove()

    const message = data.message || 'Authentication required. Please set up your Claude credentials to continue.'
    const bannerHtml = `
      <div id="auth-error-banner" class="alert alert-warning mb-4 shadow-lg">
        <div class="flex items-center gap-3 w-full">
          <i class="fas fa-exclamation-triangle text-xl"></i>
          <div class="flex-1">
            <h4 class="font-semibold">Authentication Required</h4>
            <p class="text-sm opacity-80">${this.escapeHtml(message)}</p>
          </div>
          <button class="btn btn-sm btn-primary" onclick="document.getElementById('claude-auth-modal').showModal()">
            <i class="fas fa-key mr-1"></i>
            Set Up Authentication
          </button>
        </div>
      </div>
    `

    // Insert at the top of the messages area
    if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.insertAdjacentHTML('afterbegin', bannerHtml)
    }

    // Also auto-open the modal
    const modal = document.getElementById('claude-auth-modal')
    if (modal) modal.showModal()
  }

  hideAuthErrorBanner() {
    const banner = document.getElementById('auth-error-banner')
    if (banner) banner.remove()
  }

  openAuthModal() {
    const modal = document.getElementById('claude-auth-modal')
    if (modal) modal.showModal()
  }

  closeAuthModal() {
    const modal = document.getElementById('claude-auth-modal')
    if (modal) modal.close()
  }

  // Switch between OAuth and API Key tabs in the auth modal
  switchAuthTab(event) {
    const tab = event.currentTarget.dataset.authTab
    // Update tab buttons
    document.querySelectorAll('[data-auth-tab]').forEach(btn => {
      btn.classList.toggle('tab-active', btn.dataset.authTab === tab)
    })
    // Update tab content
    document.querySelectorAll('[data-auth-panel]').forEach(panel => {
      panel.classList.toggle('hidden', panel.dataset.authPanel !== tab)
    })
  }

  // Start OAuth flow
  async startOAuthFlow() {
    const btn = document.getElementById('oauth-connect-btn')
    const errorEl = document.getElementById('auth-modal-error')
    errorEl.classList.add('hidden')

    btn.disabled = true
    btn.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Connecting...'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch('/oauth/claude/authorize', {
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'same-origin'
      })
      const data = await response.json()

      if (data.success && data.auth_url) {
        // Store code_verifier for the exchange step
        this._oauthCodeVerifier = data.code_verifier || null

        // Show the OAuth steps UI
        document.getElementById('oauth-initial').classList.add('hidden')
        document.getElementById('oauth-steps').classList.remove('hidden')

        // Set the auth URL on the link
        const authLink = document.getElementById('oauth-auth-link')
        authLink.href = data.auth_url
      } else {
        this.showAuthModalError('Failed to start OAuth flow. Please try again.')
      }
    } catch (error) {
      this.showAuthModalError('Failed to start OAuth flow: ' + (error.message || 'Unknown error'))
    } finally {
      btn.disabled = false
      btn.innerHTML = '<i class="fas fa-link mr-2"></i> Connect Claude Max Account'
    }
  }

  // Exchange OAuth authorization code
  async exchangeOAuthCode(event) {
    event.preventDefault()
    const codeInput = document.getElementById('oauth-code-input')
    const code = codeInput.value.trim()
    if (!code) {
      this.showAuthModalError('Please paste the authorization code from Claude')
      return
    }

    const btn = document.getElementById('oauth-exchange-btn')
    const errorEl = document.getElementById('auth-modal-error')
    errorEl.classList.add('hidden')
    btn.disabled = true
    btn.innerHTML = '<span class="loading loading-spinner loading-xs"></span> Connecting...'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch('/oauth/claude/exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          authorization_code: code,
          code_verifier: this._oauthCodeVerifier
        })
      })
      const data = await response.json()

      if (data.success) {
        this.onAuthSuccess('Successfully connected your Claude Max account!')
      } else {
        this.showAuthModalError(data.error || 'Failed to connect. Please try again with a new code.')
      }
    } catch (error) {
      this.showAuthModalError('Failed to connect: ' + (error.message || 'Unknown error'))
    } finally {
      btn.disabled = false
      btn.innerHTML = '<i class="fas fa-check mr-1"></i> Connect'
    }
  }

  cancelOAuthFlow() {
    document.getElementById('oauth-initial').classList.remove('hidden')
    document.getElementById('oauth-steps').classList.add('hidden')
    document.getElementById('oauth-code-input').value = ''
    document.getElementById('auth-modal-error').classList.add('hidden')
    this._oauthCodeVerifier = null
  }

  // Save API key
  async saveApiKey(event) {
    event.preventDefault()
    const input = document.getElementById('api-key-input')
    const apiKey = input.value.trim()

    if (!apiKey) {
      this.showAuthModalError('Please enter an API key')
      return
    }
    if (!apiKey.startsWith('sk-ant-')) {
      this.showAuthModalError('Invalid API key format. Anthropic API keys start with "sk-ant-"')
      return
    }

    const btn = document.getElementById('api-key-submit-btn')
    const errorEl = document.getElementById('auth-modal-error')
    errorEl.classList.add('hidden')
    btn.disabled = true
    btn.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Saving...'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch('/api/v2/user/claude_code_api_key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify({ api_key: apiKey })
      })
      const data = await response.json()

      if (data.success) {
        this.onAuthSuccess('API key saved successfully!')
      } else {
        this.showAuthModalError(data.error || 'Failed to save API key')
      }
    } catch (error) {
      this.showAuthModalError('Failed to save API key: ' + (error.message || 'Unknown error'))
    } finally {
      btn.disabled = false
      btn.innerHTML = '<i class="fas fa-save mr-2"></i> Save API Key'
    }
  }

  onAuthSuccess(message) {
    // Close modal
    this.closeAuthModal()
    // Remove auth error banner
    this.hideAuthErrorBanner()
    // Show success toast
    this.showSuccess(message)
    // Re-enable input
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
      this.inputTarget.placeholder = "Tell Claude Code what you'd like to do..."
      this.inputTarget.focus()
    }
  }

  showAuthModalError(message) {
    const errorEl = document.getElementById('auth-modal-error')
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove('hidden')
    }
  }
}